import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Mic, Sparkles, Globe, Book, User, RefreshCw,
  ExternalLink, Copy, Check, AlertCircle, ChevronRight,
  FileText, MessageSquare, Heart, Clock, Share2, Calendar, MapPin, Settings, ShieldAlert, ShieldCheck, Lock, Download, Trash2, X
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import FamilyShareModal from '../components/FamilyShareModal';

export default function ResidentDetailPage() {
  const navigate = useNavigate();
  const { communityId, storyId } = useParams();
  const { user } = useAuth();
  const functions = getFunctions();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('profile');

  // AI generation state
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(null);

  // Family portal state
  const [showFamilyPortal, setShowFamilyPortal] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Care level state
  const [editingCareLevel, setEditingCareLevel] = useState(false);
  const [communityCareLevels, setCommunityCareLevels] = useState([]);
  const [savingCareLevel, setSavingCareLevel] = useState(false);

  // Data rights (export / delete)
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Activity recommendations state
  const [recommendations, setRecommendations] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsError, setRecsError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadStory();
  }, [storyId]);

  const loadStory = async () => {
    try {
      setLoading(true);
      const storyDoc = await getDoc(doc(db, 'lifeStories', storyId));
      if (storyDoc.exists()) {
        const storyData = { id: storyDoc.id, ...storyDoc.data() };
        
        // Load resident doc for roomNumber, careLevel
        if (communityId && storyData.communityResidentId) {
          const residentDoc = await getDoc(
            doc(db, 'communities', communityId, 'residents', storyData.communityResidentId)
          );
          if (residentDoc.exists()) {
            const rd = residentDoc.data();
            storyData.roomNumber = rd.roomNumber || null;
            storyData.careLevel = rd.careLevel || storyData.careLevel || null;
          }
        }

        setStory(storyData);

        // Load community care levels
        if (communityId) {
          const communityDoc = await getDoc(doc(db, 'communities', communityId));
          if (communityDoc.exists()) {
            setCommunityCareLevels(communityDoc.data().careLevels || []);
          }
        }
      } else {
        setError('Story not found');
      }
    } catch (err) {
      console.error('Error loading story:', err);
      setError('Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  // AI Summary generation
  const handleGenerateSummaries = async () => {
    setGenerating(true);
    setGenerateError(null);
    setGenerateSuccess(null);

    try {
      const generateAISummaries = httpsCallable(functions, 'generateAISummaries');
      await generateAISummaries({ storyId });
      await loadStory();
      setGenerateSuccess('AI insights generated successfully!');
      setTimeout(() => setGenerateSuccess(null), 5000);
    } catch (err) {
      console.error('Error generating summaries:', err);
      setGenerateError(err.message || 'Failed to generate summaries');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleCareLevelChange = async (newLevel) => {
    setSavingCareLevel(true);
    try {
      const updateResident = httpsCallable(functions, 'updateCommunityResident');
      await updateResident({
        communityId,
        residentId: story.communityResidentId,
        updates: { careLevel: newLevel || null }
      });
      setStory(prev => ({ ...prev, careLevel: newLevel || null }));
      setEditingCareLevel(false);
    } catch (err) {
      console.error('Error updating care level:', err);
    } finally {
      setSavingCareLevel(false);
    }
  };

  // ── Activity recommendations ──
  const loadRecommendations = async (forceRefresh = false) => {
    setRecsLoading(true);
    setRecsError(null);
    try {
      const fn = httpsCallable(functions, 'getActivityRecommendations', { timeout: 120000 });
      const result = await fn({ storyId, forceRefresh });
      if (result.data.success) {
        setRecommendations(result.data);
      }
    } catch (err) {
      const msg = err.message || 'Failed to load recommendations';
      if (msg.includes('joy & purpose') || msg.includes('Generate AI')) {
        setRecsError('needs-insights');
      } else if (msg.includes('No upcoming') || msg.includes('calendar')) {
        setRecsError('no-events');
      } else {
        setRecsError(msg);
      }
    } finally {
      setRecsLoading(false);
    }
  };

  // Auto-load recommendations when switching to activities tab
  useEffect(() => {
    if (activeTab !== 'activities' || !story?.communityId) return;

    // Seed from cached data on story doc immediately
    if (!recommendations && story.activityRecommendations?.recommendations?.length > 0) {
      setRecommendations({
        recommendations: story.activityRecommendations.recommendations,
        avoid: story.activityRecommendations.avoid || [],
        generalNotes: story.activityRecommendations.generalNotes || null,
        generatedAt: story.activityRecommendations.generatedAt,
        cached: true
      });
    }

    // Fetch (returns cache if < 6 hrs old)
    if (!recommendations && !recsLoading) {
      loadRecommendations(false);
    }
  }, [activeTab, story?.id]);

  // Group recommendations by day, sorted chronologically
  const groupedByDay = useMemo(() => {
    if (!recommendations?.recommendations?.length) return [];

    let recs = recommendations.recommendations;
    if (typeFilter !== 'all') {
      recs = recs.filter(r => r.type === typeFilter);
    }

    const sorted = [...recs].sort((a, b) => {
      const da = stripTZ(a.dtstart) || '9999';
      const db2 = stripTZ(b.dtstart) || '9999';
      return da.localeCompare(db2);
    });

    const groups = [];
    let currentDay = null;
    let currentGroup = null;

    for (const rec of sorted) {
      const dayKey = (stripTZ(rec.dtstart) || '').slice(0, 10) || 'unknown';
      if (dayKey !== currentDay) {
        currentDay = dayKey;
        currentGroup = { dayKey, label: formatDayLabel(dayKey), items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push(rec);
    }
    return groups;
  }, [recommendations, typeFilter]);

  // ── Data rights: export & delete ──
  const handleExportData = async () => {
    setExporting(true);
    try {
      const fn = httpsCallable(functions, 'exportResidentData', { timeout: 120000 });
      const res = await fn({ storyId });
      const blob = new Blob([JSON.stringify(res.data.export, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lifestory-export-${storyId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteData = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const fn = httpsCallable(functions, 'deleteResidentData', { timeout: 300000 });
      await fn({ storyId, confirmName: deleteName });
      navigate(communityId ? `/community/${communityId}` : '/');
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Deletion failed. Please try again.');
      setDeleting(false);
    }
  };

  const residentName = story?.preferredName || story?.residentName || 'Resident';
  const summaries = story?.aiSummaries;
  const familyPortal = story?.familyPortal;
  const completionPct = story?.completionPercentage || 0;
  const answeredCount = story?.answeredCount || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading resident...</p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">{error || 'Story not found'}</h2>
          <button onClick={() => navigate(-1)} className="text-quiltt-purple hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/community/${communityId}`)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {/* Resident info */}
            <div className="flex items-center gap-4 flex-1">
              <div className="w-14 h-14 rounded-full bg-quiltt-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {story.profilePhotoUrl ? (
                  <img src={story.profilePhotoUrl} alt={residentName} className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <User className="w-7 h-7 text-quiltt-purple" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-gray-900 truncate">{residentName}</h1>
                {story.residentName && story.preferredName && story.preferredName !== story.residentName && (
                  <p className="text-sm text-gray-500">{story.residentName}</p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-20 bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${
                          completionPct >= 75 ? 'bg-green-500' :
                          completionPct >= 25 ? 'bg-quiltt-purple' : 'bg-gray-400'
                        }`}
                        style={{ width: `${Math.max(2, completionPct)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{completionPct}%</span>
                  </div>
                  <span className="text-xs text-gray-400">{answeredCount} questions answered</span>
                </div>
              </div>
            </div>

            <button
              onClick={loadStory}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      {story?.communityId && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'profile'
                  ? 'border-quiltt-purple text-quiltt-purple'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === 'activities'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Activities
              {story.activityRecommendations?.recommendations?.length > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === 'activities' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {story.activityRecommendations.recommendations.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ===== PROFILE TAB ===== */}
      {activeTab === 'profile' && (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* Messages */}
        {generateSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <Check className="w-4 h-4 flex-shrink-0" />
            {generateSuccess}
          </div>
        )}
        {generateError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {generateError}
          </div>
        )}

        {/* ===== Resident Info Card ===== */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Full Name</p>
              <p className="text-sm text-gray-900">{story?.residentName || '—'}</p>
            </div>
            {story?.preferredName && story.preferredName !== story.residentName && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Preferred Name</p>
                <p className="text-sm text-gray-900">{story.preferredName}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Room</p>
              <p className="text-sm text-gray-900">{story?.roomNumber || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Level of Care</p>
              {editingCareLevel ? (
                <select
                  value={story?.careLevel || ''}
                  onChange={(e) => handleCareLevelChange(e.target.value)}
                  disabled={savingCareLevel}
                  onBlur={() => !savingCareLevel && setEditingCareLevel(false)}
                  autoFocus
                  className="text-sm px-2 py-1 border border-indigo-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/30 w-full"
                >
                  <option value="">Not set</option>
                  {communityCareLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => communityCareLevels.length > 0 && setEditingCareLevel(true)}
                  className={`text-sm text-left ${
                    story?.careLevel
                      ? 'text-gray-900 hover:text-indigo-600'
                      : 'text-gray-400 hover:text-indigo-500'
                  } ${communityCareLevels.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}
                  title={communityCareLevels.length > 0 ? 'Click to change' : 'Configure care levels in Community Settings first'}
                >
                  {story?.careLevel || (communityCareLevels.length > 0 ? 'Click to set…' : 'Not configured')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Action Cards ===== */}

        {/* Continue Interview */}
        <button
          onClick={() => navigate(`/story/${storyId}`)}
          className="w-full bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-quiltt-purple/30 transition-all p-5 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-quiltt-50 flex items-center justify-center flex-shrink-0 group-hover:bg-quiltt-purple/10 transition-colors">
              <Mic className="w-6 h-6 text-quiltt-purple" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">
                {completionPct > 0 ? 'Continue Interview' : 'Start Interview'}
              </h3>
              <p className="text-sm text-gray-500">
                {completionPct > 0
                  ? `${completionPct}% complete — pick up where you left off`
                  : 'Begin recording life story questions'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-quiltt-purple transition-colors" />
          </div>
        </button>

        {/* AI Insights */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                summaries ? 'bg-purple-50' : 'bg-gray-100'
              }`}>
                <Sparkles className={`w-6 h-6 ${summaries ? 'text-purple-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">AI Insights</h3>
                <p className="text-sm text-gray-500">
                  {summaries
                    ? 'Biography, Quiltt Card, and conversation starters'
                    : answeredCount >= 5
                      ? 'Ready to generate — enough responses recorded'
                      : `Need at least 5 responses (${answeredCount} so far)`}
                </p>
              </div>
              <button
                onClick={handleGenerateSummaries}
                disabled={generating || answeredCount < 5}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                  generating || answeredCount < 5
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : summaries
                      ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                      : 'bg-quiltt-purple text-white hover:bg-purple-700'
                }`}
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : summaries ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Regenerate
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Summary details when available */}
          {summaries && (
            <div className="border-t border-gray-100">
              {/* About preview */}
              <div className="px-5 py-4 border-b border-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">About</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(summaries.about, 'about')}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                  >
                    {copiedField === 'about' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <p className="text-sm text-gray-600 line-clamp-3">{summaries.about}</p>
              </div>

              {/* Interests */}
              {summaries.interests?.length > 0 && (
                <div className="px-5 py-4 border-b border-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Interests</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(summaries.interests.join(', '), 'interests')}
                      className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                    >
                      {copiedField === 'interests' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {summaries.interests.slice(0, 8).map((interest, i) => (
                      <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full">
                        {interest}
                      </span>
                    ))}
                    {summaries.interests.length > 8 && (
                      <span className="text-xs text-gray-400 px-2 py-1">+{summaries.interests.length - 8} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* Joy & Purpose */}
              {summaries.joyAndPurpose && (summaries.joyAndPurpose.joyTriggers?.length > 0 || summaries.joyAndPurpose.purposeDrivers?.length > 0) && (
                <div className="px-5 py-4 border-b border-gray-50">
                  {summaries.joyAndPurpose.joyTriggers?.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">☀️</span>
                        <span className="text-sm font-medium text-gray-700">What Brings Joy</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {summaries.joyAndPurpose.joyTriggers.slice(0, 6).map((trigger, i) => (
                          <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full" title={trigger.detail}>
                            {trigger.theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {summaries.joyAndPurpose.purposeDrivers?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">🎯</span>
                        <span className="text-sm font-medium text-gray-700">What Gives Purpose</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {summaries.joyAndPurpose.purposeDrivers.slice(0, 5).map((driver, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full" title={driver.detail}>
                            {driver.theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Links row */}
              <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
                {summaries.careCardUrl && (
                  <a
                    href={summaries.careCardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-quiltt-purple hover:text-purple-700"
                  >
                    <FileText className="w-4 h-4" />
                    Quiltt Card
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {summaries.conversationStartersUrl && (
                  <a
                    href={summaries.conversationStartersUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-quiltt-purple hover:text-purple-700"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Conversation Starters
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <button
                  onClick={() => navigate(`/story/${storyId}/summaries`)}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 ml-auto"
                >
                  View full details
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Family Portal */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              familyPortal ? 'bg-green-50' : 'bg-gray-100'
            }`}>
              <Globe className={`w-6 h-6 ${familyPortal ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">Family Portal</h3>
              <p className="text-sm text-gray-500">
                {familyPortal
                  ? 'Active — family can view stories and contribute'
                  : 'Create a shareable link for family members'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {familyPortal?.url && (
                <button
                  onClick={() => copyToClipboard(familyPortal.url, 'portalUrl')}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {copiedField === 'portalUrl' ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy Link</>}
                </button>
              )}
              <button
                onClick={() => setShowFamilyPortal(true)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  familyPortal
                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                    : 'bg-quiltt-purple text-white hover:bg-purple-700'
                }`}
              >
                {familyPortal ? 'Manage' : 'Create'}
              </button>
            </div>
          </div>
        </div>

        {/* Contributions */}
        <button
          onClick={() => navigate(`/story/${storyId}/contributions`)}
          className="w-full bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all p-5 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-50 transition-colors">
              <Share2 className="w-6 h-6 text-gray-500 group-hover:text-blue-600 transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">Family Contributions</h3>
              <p className="text-sm text-gray-500">Review stories and photos shared by family members</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
          </div>
        </button>

        {/* Care & Comfort Supplement (staff-completed, sensitive) */}
        <button
          onClick={() => navigate(`/community/${communityId}/resident/${storyId}/care-supplement`)}
          className="w-full bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all p-5 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">Care &amp; Comfort Supplement</h3>
                {story?.careComfortConsent?.granted && !story?.careComfortConsent?.revokedAt ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    <ShieldCheck className="w-3 h-3" /> Consent on file
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" /> Consent needed
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">Staff-completed care-planning notes · sensitive, consent-gated</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-amber-500 transition-colors" />
          </div>
        </button>

        {/* Print Shop */}
        <button
          onClick={() => navigate(`/story/${storyId}/print`)}
          className="w-full bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all p-5 text-left group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-50 transition-colors">
              <Book className="w-6 h-6 text-gray-500 group-hover:text-amber-600 transition-colors" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">Print Shop</h3>
              <p className="text-sm text-gray-500">Preview and order a printed life story book</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
          </div>
        </button>

        {/* Data & Privacy (rights requests) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">Data &amp; Privacy</h3>
              <p className="text-sm text-gray-500">Export or delete this resident&rsquo;s data for a privacy request</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={handleExportData}
              disabled={exporting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {exporting
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Preparing export…</>
                : <><Download className="w-4 h-4" /> Export data (JSON)</>}
            </button>
            <button
              onClick={() => { setDeleteName(''); setDeleteError(null); setShowDelete(true); }}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" /> Delete data
            </button>
          </div>
        </div>

        {/* Last updated */}
        {story.updatedAt && (
          <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 pt-2">
            <Clock className="w-3 h-3" />
            Last updated {story.updatedAt?.toDate ? story.updatedAt.toDate().toLocaleDateString() : 'recently'}
          </div>
        )}
      </div>
      )}

      {/* ===== ACTIVITIES TAB ===== */}
      {activeTab === 'activities' && (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* Loading — first load */}
        {recsLoading && !recommendations && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-700 font-medium">Analyzing activities…</p>
            <p className="text-sm text-gray-500 mt-1">Matching {residentName}'s joy & purpose profile to the upcoming schedule</p>
          </div>
        )}

        {/* Error: needs insights */}
        {!recsLoading && recsError === 'needs-insights' && (
          <div className="bg-white rounded-xl border border-amber-200 p-6 text-center">
            <Calendar className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-1">AI Insights Needed</h3>
            <p className="text-sm text-gray-500 mb-4">
              Generate AI Insights on the Profile tab first — the joy & purpose analysis is needed for activity matching.
            </p>
            <button
              onClick={() => setActiveTab('profile')}
              className="text-sm text-quiltt-purple hover:underline font-medium"
            >
              Go to Profile
            </button>
          </div>
        )}

        {/* Error: no events */}
        {!recsLoading && recsError === 'no-events' && (
          <div className="bg-white rounded-xl border border-amber-200 p-6 text-center">
            <Calendar className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-1">No Calendar Events</h3>
            <p className="text-sm text-gray-500 mb-4">
              No upcoming activities found. Make sure calendar feeds are synced and {residentName}'s care level is set.
            </p>
            <button
              onClick={() => navigate(`/community/${communityId}/settings`)}
              className="inline-flex items-center gap-1.5 text-sm text-quiltt-purple hover:underline font-medium"
            >
              <Settings className="w-3.5 h-3.5" />
              Community Settings
            </button>
          </div>
        )}

        {/* Other error */}
        {!recsLoading && recsError && recsError !== 'needs-insights' && recsError !== 'no-events' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {recsError}
          </div>
        )}

        {/* Results */}
        {recommendations?.recommendations?.length > 0 && (
          <>
            {/* Filter bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500">
                {recommendations.recommendations.length} activities — next 7 days
              </span>
              <div className="flex items-center gap-1 ml-auto">
                {['all', 'joy', 'purpose', 'both'].map(t => (
                  <button
                    key={t}
                    onClick={() => setTypeFilter(t)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                      typeFilter === t
                        ? t === 'joy' ? 'bg-amber-100 text-amber-700'
                          : t === 'purpose' ? 'bg-blue-100 text-blue-700'
                          : t === 'both' ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-200 text-gray-700'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {t === 'all' ? 'All' : t === 'joy' ? '☀️ Joy' : t === 'purpose' ? '🎯 Purpose' : '✨ Both'}
                  </button>
                ))}
                <button
                  onClick={() => loadRecommendations(true)}
                  disabled={recsLoading}
                  className="ml-2 p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                  title="Refresh recommendations"
                >
                  <RefreshCw className={`w-4 h-4 ${recsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Day groups */}
            {groupedByDay.map(group => (
              <div key={group.dayKey}>
                {/* Day header */}
                <div className="flex items-center gap-2 mb-2 mt-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{group.label}</h2>
                  <span className="text-xs text-gray-400">{group.items.length} {group.items.length === 1 ? 'activity' : 'activities'}</span>
                </div>

                {/* Activities for this day */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {group.items.map((rec, i) => (
                    <div key={i} className={`p-4 flex items-start gap-3 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        rec.type === 'joy' ? 'bg-amber-50' :
                        rec.type === 'purpose' ? 'bg-blue-50' : 'bg-purple-50'
                      }`}>
                        <span className="text-lg">
                          {rec.type === 'joy' ? '☀️' : rec.type === 'purpose' ? '🎯' : '✨'}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">{rec.eventName}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            rec.score === 'high'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {rec.score === 'high' ? 'Strong' : 'Good'}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            rec.type === 'joy' ? 'bg-amber-50 text-amber-600' :
                            rec.type === 'purpose' ? 'bg-blue-50 text-blue-600' :
                            'bg-purple-50 text-purple-600'
                          }`}>
                            {rec.type === 'both' ? 'Joy + Purpose' : rec.type === 'joy' ? 'Joy' : 'Purpose'}
                          </span>
                        </div>

                        {/* Time and location */}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {rec.dtstart && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(rec.dtstart)}
                              {rec.dtend && ` – ${formatTime(rec.dtend)}`}
                            </span>
                          )}
                          {rec.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {rec.location}
                            </span>
                          )}
                        </div>

                        <p className="text-sm text-gray-600 mt-1.5">{rec.reason}</p>

                        {rec.profileConnection && (
                          <p className="text-xs text-gray-400 mt-1">
                            Connects to: {rec.profileConnection}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Filtered to empty */}
            {groupedByDay.length === 0 && typeFilter !== 'all' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-sm text-gray-500">
                  No {typeFilter === 'joy' ? 'joy' : typeFilter === 'purpose' ? 'purpose' : 'joy + purpose'} activities found.{' '}
                  <button onClick={() => setTypeFilter('all')} className="text-quiltt-purple hover:underline">Show all</button>
                </p>
              </div>
            )}

            {/* Activities to avoid */}
            {recommendations.avoid?.length > 0 && (
              <div className="bg-white rounded-xl border border-red-100 overflow-hidden">
                <div className="px-4 py-3 bg-red-50/50 border-b border-red-100">
                  <h3 className="text-sm font-semibold text-red-700">Consider Avoiding</h3>
                </div>
                {recommendations.avoid.map((item, i) => (
                  <div key={i} className={`px-4 py-3 flex items-start gap-2 ${i > 0 ? 'border-t border-red-50' : ''}`}>
                    <span className="text-red-400 mt-0.5">✕</span>
                    <div>
                      <span className="text-sm font-medium text-gray-700">{item.eventName}</span>
                      <p className="text-sm text-gray-500">{item.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* General notes */}
            {recommendations.generalNotes && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Activity Notes</p>
                <p className="text-sm text-gray-600">{recommendations.generalNotes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-xs text-gray-400 py-2">
              {recsLoading ? 'Refreshing…' : recommendations.cached ? 'Showing cached results' : 'Freshly generated'}
              {' • '}
              <button
                onClick={() => loadRecommendations(true)}
                disabled={recsLoading}
                className="text-quiltt-purple hover:underline"
              >
                Refresh
              </button>
            </div>
          </>
        )}

        {/* No results */}
        {!recsLoading && !recsError && recommendations?.recommendations?.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-900 mb-1">No Strong Matches</h3>
            <p className="text-sm text-gray-500">
              No activities in the next 7 days closely match {residentName}'s interests. Try syncing calendar feeds or adjusting their care level.
            </p>
          </div>
        )}
      </div>
      )}

      {/* Family Portal Modal */}
      {showFamilyPortal && (
        <FamilyShareModal
          isOpen={showFamilyPortal}
          onClose={() => setShowFamilyPortal(false)}
          storyId={storyId}
          storyName={residentName}
        />
      )}

      {/* Delete confirmation modal */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !deleting && setShowDelete(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" /> Delete resident data
              </h2>
              <button onClick={() => !deleting && setShowDelete(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This permanently deletes <strong>all</strong> of {residentName}&rsquo;s data — recordings, transcripts,
              photos, contributions, care notes, summaries, books, and family-portal links. This cannot be undone.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type the resident&rsquo;s name (<span className="font-semibold">{residentName}</span>) to confirm:
            </label>
            <input
              type="text"
              value={deleteName}
              onChange={(e) => setDeleteName(e.target.value)}
              placeholder={residentName}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400/40 focus:border-red-400 outline-none"
            />
            {deleteError && (
              <p className="text-sm text-red-600 mt-2">{deleteError}</p>
            )}
            <div className="flex items-center justify-end gap-3 mt-5">
              <button onClick={() => !deleting && setShowDelete(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button
                onClick={handleDeleteData}
                disabled={deleting || deleteName.trim().toLowerCase() !== residentName.trim().toLowerCase()}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                {deleting
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Deleting…</>
                  : <>Permanently delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================
// Date helpers
// ============================================

function stripTZ(dtstr) {
  if (!dtstr) return null;
  return dtstr.replace(/\s*\[.*\]$/, '').replace(/Z$/, '');
}

function formatDayLabel(dayKey) {
  if (dayKey === 'unknown') return 'Unscheduled';
  try {
    const d = new Date(dayKey + 'T12:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayKey = today.toISOString().slice(0, 10);
    const tomorrowKey = tomorrow.toISOString().slice(0, 10);

    const formatted = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    if (dayKey === todayKey) return 'Today — ' + formatted;
    if (dayKey === tomorrowKey) return 'Tomorrow — ' + formatted;
    return formatted;
  } catch {
    return dayKey;
  }
}

function formatTime(dtstr) {
  if (!dtstr) return '';
  try {
    const d = new Date(stripTZ(dtstr));
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}
